import { useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Poll every 45s — server snapshot has a 60s TTL cache so this stays cheap.
const POLL_MS = 45_000;
const STORAGE_PREFIX = "msc_revenue_pulse_";

function fmtUsd(cents) {
  const dollars = Math.round((cents || 0) / 100);
  return `$${dollars.toLocaleString()}`;
}

// Synthesize a short two-note "cha-ching" chime via Web Audio (no external asset).
function playChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const t0 = ctx.currentTime;
    const notes = [
      { f: 880, t: 0.0, d: 0.14 }, // A5
      { f: 1318.5, t: 0.11, d: 0.24 }, // E6
    ];
    notes.forEach((n) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(n.f, t0 + n.t);
      gain.gain.setValueAtTime(0, t0 + n.t);
      gain.gain.linearRampToValueAtTime(0.09, t0 + n.t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + n.t + n.d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0 + n.t);
      osc.stop(t0 + n.t + n.d + 0.05);
    });
    setTimeout(() => {
      try { ctx.close(); } catch (e) { /* ignore */ }
    }, 800);
  } catch (e) {
    // Silent — autoplay policy or unsupported browser.
  }
}

/**
 * Background poller for the team scoreboard (admin + rep + office_manager).
 * Polls GET /api/dashboard/pulse every 45s + on tab focus.
 *
 * Fires a sonner toast + a subtle chime when the number of active subscriptions
 * (or MRR total) increases. Reps additionally get "Your cut +$X/mo" appended
 * to the toast whenever THEIR share of MRR ticks up (i.e. one of their
 * attributed subs landed).
 *
 * Baseline is persisted per-user in localStorage so a "you missed 2 subs while
 * away" moment survives page reloads.
 */
export function useRevenuePulse() {
  const { user } = useAuth();
  const baselineRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Browsers block AudioContext until a user gesture — track when we're allowed to chime.
  useEffect(() => {
    const unlock = () => {
      audioUnlockedRef.current = true;
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  useEffect(() => {
    if (!user || user.must_change_password) return;
    if (!["admin", "rep", "office_manager"].includes(user.role)) return;

    const storageKey = STORAGE_PREFIX + user.id;

    // Seed baseline from localStorage so we can detect events across reloads.
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) baselineRef.current = JSON.parse(raw);
    } catch (e) {
      // ignore
    }

    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/dashboard/pulse");
        if (cancelled || !data) return;
        if (data.connected === false || data.error) return;

        const next = {
          active: data.active_count || 0,
          mrr: data.mrr_cents || 0,
          mine_active: data.my_attributed_active_count || 0,
          mine_mrr: data.my_share_mrr_cents || 0,
        };
        const prev = baselineRef.current;

        if (prev) {
          const subDelta = next.active - prev.active;
          const mrrDelta = next.mrr - prev.mrr;
          const myMrrDelta = next.mine_mrr - (prev.mine_mrr || 0);
          const myActiveDelta = next.mine_active - (prev.mine_active || 0);

          if (subDelta > 0) {
            if (audioUnlockedRef.current) playChime();
            // Build description: team hit + optional "your cut" tail
            const parts = [];
            parts.push(
              mrrDelta > 0
                ? `+${fmtUsd(mrrDelta)}/mo · Now ${fmtUsd(next.mrr)}/mo across ${next.active} active`
                : `Now ${fmtUsd(next.mrr)}/mo across ${next.active} active`
            );
            if (myMrrDelta > 0) {
              parts.push(
                myActiveDelta > 0
                  ? `Your cut +${fmtUsd(myMrrDelta)}/mo — nice work.`
                  : `Your cut +${fmtUsd(myMrrDelta)}/mo`
              );
            }
            const title =
              subDelta === 1
                ? "New subscription just landed"
                : `${subDelta} new subscriptions just landed`;
            toast.success(title, {
              description: parts.join(" · "),
              duration: 9000,
              id: `revenue-pulse-${Date.now()}`,
            });
          } else if (mrrDelta > 0) {
            // Upgrade with no new sub count.
            if (audioUnlockedRef.current) playChime();
            const desc =
              myMrrDelta > 0
                ? `Now ${fmtUsd(next.mrr)}/mo · Your cut +${fmtUsd(myMrrDelta)}/mo`
                : `Now ${fmtUsd(next.mrr)}/mo across ${next.active} active`;
            toast.success(`MRR up ${fmtUsd(mrrDelta)}/mo`, {
              description: desc,
              duration: 7000,
              id: `revenue-pulse-mrr-${Date.now()}`,
            });
          }
        }

        baselineRef.current = next;
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch (e) {
          // ignore quota errors
        }
      } catch (e) {
        // silent — 401 / network hiccup / cold start
      }
    };

    tick();
    const timer = setInterval(tick, POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    const onFocus = () => tick();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [user]);
}
